import { createContext, useContext, type ReactNode } from "react";
import { createPortal } from "react-dom";

/** The header slots for a page's controls, set by AppTopBar. */
export interface TopBarSlots {
  /** Left of the page name: a back link, as the desktop app's viewer has. */
  leading: HTMLElement | null;
  /** Right of the page name: the page's controls. */
  trailing: HTMLElement | null;
  /** Page-wide controls use the header on phones too. */
  page?: HTMLElement | null;
}

export const TopBarSlotContext = createContext<TopBarSlots>({ leading: null, trailing: null });

/**
 * Put a page's controls in the top bar, as the desktop app's episode
 * viewer does: a back link before the name ("leading"), and its rating,
 * previous and next, and Details after it ("trailing", the default).
 * On a phone, leading and trailing controls render in the page. The page
 * placement keeps page-wide controls in the header at every width.
 */
export function TopBarActions({
  children,
  placement = "trailing",
}: {
  children: ReactNode;
  placement?: keyof TopBarSlots;
}) {
  const slot = useContext(TopBarSlotContext)[placement];
  return slot ? createPortal(children, slot) : <>{children}</>;
}
